/**
 * Email templates — `email-templates.html` ported. A library-and-editor split
 * screen rather than a card gallery: the prototype browses by recognising a
 * name in a list while editing the one that's open, not by scanning cards.
 */
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../hooks/useAuth.js';
import { useDebounce } from '../../hooks/useDebounce.js';
import {
  Card, Modal, SearchInput, ErrorState, EmptyState, MockBadge,
} from '../../components/common/index.jsx';
import { ButtonSpinner, Spinner } from '../../components/common/Loading.jsx';
import { isMock } from '../../api/mockFallback.js';
import EmailTemplateEditor from './EmailTemplateEditor.jsx';
import {
  useTemplates, useTemplateFolders, useCreateTemplate, useUpdateTemplate, useDeleteTemplate,
} from './useEmailTemplates.js';

/** A free name for a clone: "X (copy)", then "X (copy 2)", "X (copy 3)"... */
function freeCloneName(base, taken) {
  let candidate = `${base} (copy)`;
  let n = 2;
  while (taken(candidate)) {
    candidate = `${base} (copy ${n})`;
    n += 1;
  }
  return candidate;
}

export default function EmailTemplatesPage() {
  const { can } = useAuth();
  const canCreate = can('templates', 'create');
  const canEdit = can('templates', 'edit');
  const canDelete = can('templates', 'delete');

  const [search, setSearch] = useState('');
  const [folderFilter, setFolderFilter] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [draftSeed, setDraftSeed] = useState(null);
  const [draftNonce, setDraftNonce] = useState(0);
  const [deleteOpen, setDeleteOpen] = useState(false);
  // Bridges the gap right after a create/update: `selectedId` and `isNew` flip
  // the instant the mutation resolves, but `templates` (from `useTemplates`)
  // only catches up once its invalidated query refetches a moment later. Without
  // this, `initial` goes null for that one render and the editor — which
  // assumes a saved record means a real one — throws reading `initial.name`.
  const [pendingRecord, setPendingRecord] = useState(null);

  const debounced = useDebounce(search);
  const params = useMemo(
    () => ({ folder: folderFilter || undefined, q: debounced || undefined, limit: 100 }),
    [folderFilter, debounced],
  );

  const { data, isLoading, error, refetch } = useTemplates(params);
  const { data: folderData } = useTemplateFolders();
  const templates = data?.data ?? [];
  const folders = folderData?.data ?? [];

  const createMutation = useCreateTemplate();
  const updateMutation = useUpdateTemplate();
  const deleteMutation = useDeleteTemplate();

  // Land on the first template, or a blank draft if the library is empty —
  // matching the prototype's boot sequence — but only once, on first load.
  useEffect(() => {
    if (isLoading || selectedId !== null || draftSeed !== null) return;
    if (templates.length) setSelectedId(templates[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, templates.length]);

  // Once the list actually contains the record, the bridge is no longer needed.
  useEffect(() => {
    if (pendingRecord && templates.some((t) => t.id === pendingRecord.id)) setPendingRecord(null);
  }, [templates, pendingRecord]);

  const isNew = selectedId === null;
  const initial = isNew
    ? draftSeed
    : templates.find((t) => t.id === selectedId)
      ?? (pendingRecord?.id === selectedId ? pendingRecord : null);

  const nameTaken = (name) => templates.some(
    (t) => t.id !== selectedId && t.name.trim().toLowerCase() === name.trim().toLowerCase(),
  );

  function selectExisting(id) {
    setSelectedId(id);
    setDraftSeed(null);
  }

  function startNew() {
    setSelectedId(null);
    setDraftSeed({});
    setDraftNonce((n) => n + 1);
  }

  function startClone(source) {
    setSelectedId(null);
    setDraftSeed({
      folder: source.folder,
      name: freeCloneName(source.name, nameTaken),
      description: source.description,
      subject: source.subject,
      body_html: source.body_html,
      include_signature: source.include_signature,
    });
    setDraftNonce((n) => n + 1);
  }

  async function handleSave(payload) {
    if (isNew) {
      const created = await createMutation.mutateAsync(payload);
      setPendingRecord(created);
      selectExisting(created.id);
    } else {
      const updated = await updateMutation.mutateAsync({ id: selectedId, patch: payload, rowVersion: initial.row_version });
      setPendingRecord(updated);
    }
  }

  async function handleConfirmDelete() {
    const target = initial;
    await deleteMutation.mutateAsync({ id: target.id, rowVersion: target.row_version });
    setDeleteOpen(false);
    const rest = templates.filter((t) => t.id !== target.id);
    rest.length ? selectExisting(rest[0].id) : startNew();
  }

  const filteredCount = templates.length;
  const busy = createMutation.isPending || updateMutation.isPending;

  return (
    <>
      <div className="lv-head mb-3">
        <div className="hl-icon" style={{ background: 'var(--grad-cool)' }}>
          <i className="bi bi-envelope-paper-fill" aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <div className="eyebrow">Engage</div>
          <h1 className="page-title">Email Templates</h1>
          <div className="t-xs ink-3 mt-1">
            {filteredCount} template{filteredCount === 1 ? '' : 's'} · names are unique across the library ·
            merge fields resolve per recipient
          </div>
        </div>
        <div className="ms-auto d-flex flex-wrap gap-2 align-items-center">
          {isMock(data) && <MockBadge section="email templates" />}
          {!isNew && initial && (
            <button type="button" className="btn btn-soft btn-sm-x" onClick={() => startClone(initial)}>
              <i className="bi bi-files me-1" aria-hidden="true" />Clone
            </button>
          )}
          {canCreate && (
            <button type="button" className="btn btn-add btn-sm-x" onClick={startNew}>
              <i className="bi bi-plus-lg me-1" aria-hidden="true" />New Template
            </button>
          )}
        </div>
      </div>

      <Card bodyClass="p-0">
        <div className="tpl-layout">
          <div className="tpl-list">
            <div className="p-2 border-bottom" style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>
              <SearchInput value={search} onChange={setSearch} placeholder="Search templates…" />
              <div className="d-flex flex-wrap gap-1 mt-2">
                <button
                  type="button" className={`chip ${folderFilter === '' ? 'active' : ''}`}
                  onClick={() => setFolderFilter('')}
                >
                  All
                </button>
                {folders.map((f) => (
                  <button
                    key={f} type="button" className={`chip ${folderFilter === f ? 'active' : ''}`}
                    onClick={() => setFolderFilter(f)}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>

            {isLoading ? (
              <div className="p-3"><Spinner /></div>
            ) : error ? (
              <div className="p-3"><ErrorState error={error} onRetry={refetch} /></div>
            ) : templates.length === 0 ? (
              <EmptyState icon="bi-search" title="No templates match" message="Try a different search or folder." />
            ) : (
              templates.map((t) => (
                <div
                  key={t.id} className={`tpl-item${t.id === selectedId ? ' active' : ''}`}
                  onClick={() => selectExisting(t.id)} role="button" tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && selectExisting(t.id)}
                >
                  <div className="d-flex align-items-start gap-2">
                    <div className="min-w-0 flex-grow-1">
                      <div className="tn text-truncate">{t.name}</div>
                      <div className="ts text-truncate">{t.subject}</div>
                    </div>
                    <span className="pill pill-gray no-wrap">{t.usage_count || 0}</span>
                  </div>
                  <div className="d-flex align-items-center gap-1 mt-2">
                    <span className="folder-pill"><i className="bi bi-folder-fill" aria-hidden="true" />{t.folder}</span>
                    {!t.is_shared && (
                      <span className="pill pill-gray" title="Private to its owner">
                        <i className="bi bi-lock-fill" aria-hidden="true" />
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="tpl-editor">
            {isLoading ? (
              <div className="p-3"><Spinner /></div>
            ) : (
              <EmailTemplateEditor
                key={selectedId ?? `new:${draftNonce}`}
                initial={initial}
                isNew={isNew}
                folders={folders}
                nameTaken={nameTaken}
                busy={busy}
                canEdit={isNew ? canCreate : canEdit}
                canDelete={canDelete}
                onSave={handleSave}
                onDelete={() => setDeleteOpen(true)}
                onDuplicate={startClone}
              />
            )}
          </div>
        </div>
      </Card>

      <Modal
        open={deleteOpen} onClose={() => setDeleteOpen(false)} title="Delete template"
        footer={(
          <>
            <button type="button" className="btn btn-cancel btn-sm-x" onClick={() => setDeleteOpen(false)}>Cancel</button>
            <button
              type="button" className="btn btn-delete btn-sm-x"
              disabled={deleteMutation.isPending} onClick={handleConfirmDelete}
            >
              {deleteMutation.isPending && <ButtonSpinner />}Delete
            </button>
          </>
        )}
      >
        <p className="t-sm mb-0">
          Delete <strong>{initial?.name}</strong>? Emails already sent using it are unaffected.
        </p>
      </Modal>
    </>
  );
}
